import * as Sentry from '@sentry/react'
import { NextPageContext } from 'next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import Button from '../components/_legacy/Button'
import Col from '../components/basic/Col'
import BasicHead from '../components/common/BasicHead'
import CenterContainer from '../components/common/CenterContainer'
import TailwindWrapper from '../components/container/TailwindWrapper'
import apis from '../modules/apis'
import { axiosGet } from '../modules/axiosHelper'
import { useCurrentUser } from '../modules/context/UserContext'
import useAppRouter from '../modules/hooks/common/useAppRouter'
import useRNMsg from '../modules/hooks/webview/common/useRNMsg'
import { LOCALE_KO } from '../modules/i18n/config'
import { s_head } from '../modules/i18n/strings/auto/wording'
import urls from '../modules/urls'
import { getOgUrl, t } from '../modules/utils'
import { ROLE_STUDENT } from '../modules/vars'

type ErrorPagePropsType = {
  statusCode: any
  ogData: any
  err: any
}

type ErrorCommonPropsType = {
  statusCode: any
  err: any
}

export const Error404Component = () => {
  const router = useRouter()
  useEffect(() => {
    Sentry.captureException(new Error('존재하지 않는 경로 접근! 404 Error!!'))
  }, [])

  return (
    <TailwindWrapper>
      <CenterContainer clx="px-20">
        <div className="row flex justify-center">
          <Col clx="col-12 md:col-5 lg:col-4 my-80">
            <div className="text-center">
              <div className="mb-16 font-medium uppercase text-gray-500">404 error</div>
              <div className="text-h6 mb-80 text-gray-500">{router.pathname}</div>
              <div className="text-h6 mb-80 text-gray-500">{new Date().toString()}</div>
              <h1 className="text-d2 mb-40 font-medium">Page Not Found</h1>
              <div className="mb-40 text-gray-500">
                We can't seem to find the page you're looking for. Check the URL
              </div>
              <Button primary md>
                <Link legacyBehavior href="/">
                  Return to home
                </Link>
              </Button>
            </div>
          </Col>
        </div>
      </CenterContainer>
    </TailwindWrapper>
  )
}

const ErrorCommonCompoent = ({ statusCode, err }: ErrorCommonPropsType) => {
  useEffect(() => {
    if (err) {
      Sentry.captureException(err)
    } else {
      Sentry.captureException(new Error('Oops!, something went wrong!!!!!!!'))
    }
  }, [])

  const router = useRouter()
  return (
    <TailwindWrapper>
      <CenterContainer clx="px-20">
        <div className="row flex justify-center">
          <Col clx="col-12 md:col-5 lg:col-4 my-80">
            <div className="row text-center">
              <div className="mb-24 text-[10px] uppercase text-gray-500">
                {statusCode ? `An error ${statusCode} occurred on server` : 'An error occurred on client'}
              </div>
              <div className="text-h6 mb-80 text-gray-500">{router.pathname}</div>
              <div className="text-h6 mb-80 text-gray-500">{new Date().toString()}</div>
              <h1 className="text-d2 mb-40 font-medium">Oops, something went wrong</h1>
              <div className="mb-40 text-gray-500">
                Try to refresh this page or feel free to contact us if the problem persists.
              </div>
              <Link legacyBehavior href="/">
                <Button primary md>
                  Return to home
                </Button>
              </Link>
            </div>
          </Col>
        </div>
      </CenterContainer>
    </TailwindWrapper>
  )
}

const ErrorPage = ({ statusCode, err, ogData }: ErrorPagePropsType) => {
  console.log(err, 'err')
  const router = useAppRouter()
  const { currentUser } = useCurrentUser()
  const { sendPostMsg } = useRNMsg()
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const fetchData = async () => {
        const response = await axiosGet(`${apis.error()}?url=${window.location.href}&status_code=${statusCode}`)
        if (response) {
          if (currentUser) {
            currentUser.role === ROLE_STUDENT
              ? router.push(urls.student.newPortal.home)
              : router.push(urls.tutor.portal.home)
            return
          }
          router.push(urls.oneOnOne.index)
          return
        }
      }
      fetchData()
    }
    /**
     * 웹뷰에서 에러 발생시 RN으로 에러 메시지 전달
     */
    sendPostMsg({ message: 'webview-error' })
  }, [])

  useEffect(() => {
    if (router) {
      router.error()
    }
  }, [router])
  return (
    <>
      <BasicHead ogData={ogData} />
      {statusCode == 404 ? <Error404Component /> : <ErrorCommonCompoent statusCode={statusCode} err={err} />}
    </>
  )
}

ErrorPage.getInitialProps = async (context: NextPageContext) => {
  const { res, err, query } = context

  const statusCode = res ? res.statusCode : err ? err.statusCode : 404
  const { lang = LOCALE_KO } = query

  const ogData = {
    title: t(s_head.student.title_error_page, lang),
    ogUrl: getOgUrl({ lang, context }),
  }
  return { statusCode, err, ogData }
}

export default ErrorPage
